import { ProductDetails } from '@/modals/product-details';
import { ProductVariantDetails } from '@/models/product-variant-details/product-variant-details';
import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-product-view',
  templateUrl: './product-view.component.html',
  styleUrl: './product-view.component.scss'
})
export class ProductViewComponent implements OnInit {
  destroy$: Subject<boolean> = new Subject<boolean>();
  productDetails: ProductDetails[] = [];
  getAllProductDetails: ProductDetails[] = [];
  searchText: any;
  productNameDetails: string[] = [];
  productCodeDetails: string[] = [];
  productCreationDateDetails: string[] = [];
  productCurrentStatus: string[] = [];
  productName: string = '';
  productCode: string = '';
  productCreationDate: string = '';
  productStatus: string = '';
  isVariantModalOpen = false;
  selectedProduct: any = null;
  variantList: ProductVariantDetails[] = [];
  isSubmitting = false;
  variantTableData: any[] = [];
  categoryList: any[] = [];
  subCategoryList: any[] = [];

  categoryId: any = '';
  subCategoryId: any = '';
  getAllVariantDetailsByProductId: ProductVariantDetails[] = [];

  constructor(private appService: AppService, private toastr: ToastrService, private spinner: NgxSpinnerService,
    private datePipe: DatePipe, private router: Router) { }

  ngOnInit(): void {
    this.getProductDetails();
  }

  // get product details
  getProductDetails() {
    this.spinner.show();
    this.appService.getAllProducts()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any[]) => {
        this.spinner.hide();
        this.getAllProductDetails = data.sort((a, b) => b.productId - a.productId);
        // get unique details for dropdowns  - productName, productCode, productCreationDate, productStatus
        this.productNameDetails = [...new Set(this.getAllProductDetails.map(item => item.productName))];
        this.productCodeDetails = [...new Set(this.getAllProductDetails.map(item => item.productCode))];
        this.productCreationDateDetails = [...new Set(this.getAllProductDetails.map(item => this.datePipe.transform(item.createdAt, 'dd/MM/yyyy')).filter((date): date is string => date !== null))];
        this.productCurrentStatus = [...new Set(this.getAllProductDetails.map(item => item.availability))];
        this.categoryList = [...new Map(this.getAllProductDetails.map(item => item.categoryDetails).filter(Boolean).map(cat => [cat.categoryId, cat])).values()];
        this.productDetails = this.getAllProductDetails;
      }, (error: any) => {
        this.spinner.hide();
        this.toastr.error(error.message || "Something went wrong, please try again...")
      });
  }

  onCategoryChange(event: any) {

    const categoryId = event.target.value;
    this.categoryId = categoryId;
    this.subCategoryId = '';

    // ALL CATEGORY → SHOW ALL SUBCATEGORIES
    if (!categoryId || categoryId === 'all') {

      const allSubCategories = this.getAllProductDetails
        .flatMap(item => item.categoryDetails?.subCategories || []);

      this.subCategoryList = [
        ...new Map(
          allSubCategories.map(sub => [sub.subCategoryId, sub])
        ).values()
      ];

    }
    else {

      // FILTER BASED ON CATEGORY
      const filtered = this.getAllProductDetails.filter(
        item => item.categoryDetails?.categoryId == categoryId
      );

      const subCategories = filtered
        .flatMap(item => item.categoryDetails?.subCategories || []);

      this.subCategoryList = [
        ...new Map(
          subCategories.map(sub => [sub.subCategoryId, sub])
        ).values()
      ];

    }
    this.applyFilter();
  }


  // Current select filter value set function
  updateDetails(event: any, filterType: string) {

    const value = event.target.value;

    switch (filterType) {

      case 'name':
        this.productName = value;
        break;

      case 'category':
        this.categoryId = value;
        this.onCategoryChange(event); // important
        return; // stop further execution

      case 'subcategory':
        this.subCategoryId = value;
        break;

      case 'date':
        this.productCreationDate = value;
        break;

      case 'status':
        this.productStatus = value;
        break;

      default:
        break;
    }

    this.applyFilter();
  }

  // Apply filter function
  applyFilter() {

    let filteredData: ProductDetails[] = [...this.getAllProductDetails];

    // =========================
    // CATEGORY FILTER
    // =========================
    if (this.categoryId && this.categoryId !== 'all') {
      filteredData = filteredData.filter(
        item => item.categoryDetails?.categoryId == this.categoryId
      );
    }

    // =========================
    // SUBCATEGORY FILTER (FIXED)
    // =========================
    if (this.subCategoryId && this.subCategoryId !== 'all') {

      filteredData = filteredData.filter(item =>
        item.categoryDetails?.subCategories?.some(sub =>
          sub.subCategoryId == this.subCategoryId
        )
      );
    }

    // =========================
    // PRODUCT NAME FILTER
    // =========================
    if (this.productName && this.productName !== 'all') {
      filteredData = filteredData.filter(
        item => item.productName === this.productName
      );
    }

    // =========================
    // DATE FILTER
    // =========================
    if (this.productCreationDate && this.productCreationDate !== 'all') {
      filteredData = filteredData.filter(
        item =>
          this.datePipe.transform(item.createdAt, 'dd/MM/yyyy') ===
          this.productCreationDate
      );
    }

    // =========================
    // STATUS FILTER
    // =========================
    if (this.productStatus && this.productStatus !== 'all') {
      filteredData = filteredData.filter(
        item => item.availability === this.productStatus
      );
    }

    // FINAL RESULT
    this.productDetails = filteredData;
  }


  allowOnlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }


  // Remove filter function values
  resetReviewFilter() {
    this.searchText = '';
    this.productName = '';
    this.productCode = '';
    this.productCreationDate = '';
    this.productStatus = '';

    this.productDetails = [...this.getAllProductDetails];
    this.productNameDetails = [...new Set(this.productDetails.map(item => item.productName))];
    this.productCodeDetails = [...new Set(this.productDetails.map(item => item.productCode))];
    this.productCreationDateDetails = [...new Set(this.productDetails.map(item => this.datePipe.transform(item.createdAt, 'dd/MM/yyyy')).filter((date): date is string => date !== null))];
    this.productCurrentStatus = [...new Set(this.productDetails.map(item => item.availability))];
  }

  selectCurrentStatusUpdate(item: any) {
    this.updateAlert(item);
  }


  updateAlert(item: any) {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to update the availability status",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, updated it!"
    }).then((result) => {
      if (result.isConfirmed) {
        this.updateAvailabilityStatus(item);
      } else {
        item.availability = 'Yes'
        // window.location.reload();
      }
    });
  }

  EditAlert(item: any) {
    Swal.fire({
      title: "Are you sure?",
      html: `Are you sure you want to update the details of <b>${item.productName}</b>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.setItem("productId", item.productId);
        this.router.navigate(['/main/add-new-product']);
      } else {
        window.location.reload();
      }
    });
  }


  updateAvailabilityStatus(item: any) {
    this.spinner.show();
    const jsonData = {
      productId: item.productId,
      availability: item.availability
    }
    this.appService.updateProductAvailability(jsonData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.toastr.success("Availability status updated successfully")
        setTimeout(() => {
          window.location.reload();
          this.spinner.hide();
        }, 1000);
      }, (error: any) => {
        this.spinner.hide();
        this.toastr.error(error.message || "Something went wrong, please try again...")
      })
  }

  openVariantModal(product: any) {
    this.variantTableData = [];
    this.selectedProduct = null;
    this.getAllVariantDetailsByProductId = [];
    // if (product?.variants && product.variants.length > 0) {
    //   product.variants.forEach((variant: any) => {
    //     this.variantTableData.push({
    //       productVariantId: variant?.productVariantId,
    //       productId: variant?.productId,
    //       size: variant?.size,
    //       productMrp: variant?.productMrp,
    //       productSrp: variant?.productSrp,
    //       varientQuantity: variant?.varientQuantity,
    //       isAvailable: variant?.isAvailable,
    //       barcodeNumber: variant?.barcodeNumber
    //     });
    //   });
    // }
    this.getVariantByProductId(product?.productId);
    this.selectedProduct = product;
  }

  getVariantByProductId(productId: number) {
    this.spinner.show();
    this.appService.getVariantByProductId(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ProductVariantDetails[]) => {
        this.spinner.hide();
        this.getAllVariantDetailsByProductId = data.filter(item => item.isAvailable === true);
        // clone to prevent auto-update
        this.variantTableData = this.getAllVariantDetailsByProductId;
        this.isVariantModalOpen = true;
        
      }, (error: any) => {
        this.spinner.hide();
        this.toastr.error(error.message || "Something went wrong, please try again...")
      });
  }

  closeVariantModal() {
    this.isVariantModalOpen = false;
    this.selectedProduct = null;
    this.variantTableData = [];
  }

  confirmVariantUpdate() {
    Swal.fire({
      title: 'Confirm Update?',
      text: 'All variant changes will be saved',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Save'
    }).then(res => {
      if (res.isConfirmed) {
        this.updateSingleVariant()
      }
    });
  }

  updateSingleVariant(variant?: any) {
    this.spinner.show();
    this.isSubmitting = true;

    const payload = {
      productVariantId: variant.productVariantId,
      productMrp: variant.productMrp,
      productSrp: variant.productSrp,
      varientQuantity: variant.varientQuantity,
      isAvailable: variant.isAvailable
    };

    this.appService.updateSingleVariant(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        variant.isUpdating = false;
        this.spinner.hide();
        this.isVariantModalOpen = false;
        this.getProductDetails();
        this.toastr.success('Variant updated successfully');
      }, error => {
        variant.isUpdating = false;
        this.spinner.hide();
        this.toastr.error(error.message || 'Update failed');
      });
  }


  /** delete saved variant */
  deleteVariant(index: number, variant: any) {
    if (!variant.productVariantId) {
      this.variantList.splice(index, 1);
      return;
    }

    Swal.fire({
      title: 'Delete Variant?',
      icon: 'warning',
      showCancelButton: true
    }).then(res => {
      if (res.isConfirmed) {
        this.spinner.show();
        this.appService.deleteProductVariantDetails(variant.productVariantId)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.spinner.hide();
            this.variantList.splice(index, 1);
            this.toastr.success('Variant deleted');
          }, err => {
            this.spinner.hide();
            this.toastr.error(err.message || 'Delete failed');
          });
      }
    });
  }

  getProductVariantReportData(type: any) {
    try {
      const rows: any[] = [];

      this.productDetails.forEach(product => {

        rows.push({
          productName: product.productName || '-',
          categoryName: product.categoryDetails?.categoryName || '-',
          barcode: '',
          size: '',
          quantity: '',
          currentQty: '',
          srp: '',
          isAvailable: ''
        });

        product.variants?.forEach(variant => {
          rows.push({
            productName: '',
            categoryName: '',
            barcode: variant.barcodeNumber || '-',
            size: variant.size || '-',
            quantity: variant.varientQuantity ?? 0,
            currentQty: variant.availableStock ?? 0,
            srp: this.formatINR(variant.productSrp, type),
            isAvailable: variant.isAvailable ? 'Yes' : 'No'
          });
        });

      });

      return rows;

    } catch (e) {
      console.error(e);
      this.toastr.error('Report data generation failed');
      return [];
    }
  }
  
  deleteProduct(product: any) {

  // Product status must be No
  // if (product.availability == 'Yes') {
  //   this.toastr.warning('Product status must be No');
  //   return;
  // }

  // All variants quantity must be 0
  // const hasStock = product.variants?.some(
  //   (v: any) => Number(v.varientQuantity) > 0
  // );

  // if (hasStock) {
  //   this.toastr.warning(
  //     'All variant quantity must be 0 before delete'
  //   );
  //   return;
  // }

  Swal.fire({
    title: 'Delete Product?',
    text: 'Are you sure you want to delete this product?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, Delete',
    cancelButtonText: 'Cancel'
  }).then((result) => {

    if (result.isConfirmed) {

      this.spinner.show();

      this.appService
        .deleteProductById(product.productId)
        .subscribe({
          next: () => {

            this.spinner.hide();

            this.toastr.success(
              'Product deleted successfully'
            );

            this.getProductDetails();
          },

          error: (error : any) => {

            this.spinner.hide();

            this.toastr.error(
              error?.error?.message || 'Delete failed'
            );
          }
        });
    }
  });
}

removeVariant(variant: any) {

  if (!variant?.productVariantId) {
    this.toastr.error('Variant ID not found');
    return;
  }

  Swal.fire({
    title: 'Remove Variant?',
    text: `Do you want to remove ${variant.barcodeNumber || 'this variant'}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, Remove',
    cancelButtonText: 'Cancel'
  }).then((result) => {

    if (!result.isConfirmed) {
      return;
    }

    variant.isDeleting = true;

    this.appService
      .deleteProductVariantDetails(variant.productVariantId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({

        next: () => {

          variant.isDeleting = false;

          // Remove from popup table
          this.variantTableData =
            this.variantTableData.filter(
              v => v.productVariantId !== variant.productVariantId
            );

          this.toastr.success(
            'Variant removed successfully'
          );
        },

        error: (error: any) => {

          variant.isDeleting = false;

          this.toastr.error(
            error?.error?.message ||
            error?.message ||
            'Variant remove failed'
          );
        }

      });
  });
}

  downloadProductExcel() {
    try {
      this.spinner.show();

      const data = this.getProductVariantReportData('excel');
      if (!data.length) {
        this.toastr.warning('No data available');
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(data);

      XLSX.utils.sheet_add_aoa(worksheet, [[
        'Product Name',
        'Category',
        'Barcode',
        'Size',
        'Quantity',
        'Current Quantity',
        'SRP',
        'Available'
      ]], { origin: 'A1' });

      // Center align
      const range = XLSX.utils.decode_range(worksheet['!ref']!);
      for (let r = range.s.r; r <= range.e.r; r++) {
        for (let c = range.s.c; c <= range.e.c; c++) {
          if (worksheet[XLSX.utils.encode_cell({ r, c })]) {
            worksheet[XLSX.utils.encode_cell({ r, c })].s = {
              alignment: { horizontal: 'center', vertical: 'center' }
            };
          }
        }
      }

      const wb = { Sheets: { Report: worksheet }, SheetNames: ['Report'] };

      const buffer = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true
      });

      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        'Product_Variant_Report.xlsx'
      );

      this.toastr.success('Excel downloaded');

    } catch (e) {
      console.error(e);
      this.toastr.error('Excel download failed');
    } finally {
      this.spinner.hide();
    }
  }



  downloadProductPdf() {
    try {
      this.spinner.show();

      const data = this.getProductVariantReportData('pdf');
      if (!data.length) {
        this.toastr.warning('No data available');
        return;
      }

      const doc = new jsPDF('p', 'mm', 'a4');

      autoTable(doc, {
        head: [[
          'Product',
          'Category',
          'Barcode',
          'Size',
          'Quantity',
          'Current Qty',
          'SRP',
          'Available'
        ]],
        body: data.map(r => [
          r.productName,
          r.categoryName,
          r.barcode,
          r.size,
          r.quantity,
          r.currentQty,
          r.srp,
          r.isAvailable
        ]),
        startY: 20,
        theme: 'grid',
        styles: {
          fontSize: 9,
          halign: 'center',
          valign: 'middle'
        },
        pageBreak: 'auto',
        rowPageBreak: 'avoid',
        didDrawPage: () => {
          doc.setFontSize(12);
          doc.text('Product Variant Report', 14, 12);
        }
      });

      doc.save('Product_Variant_Report.pdf');
      this.toastr.success('PDF downloaded');

    } catch (e) {
      console.error(e);
      this.toastr.error('PDF download failed');
    } finally {
      this.spinner.hide();
    }
  }


  formatINR(value: number, type: 'excel' | 'pdf'): string {
    const formatted = Number(value || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    return type === 'pdf'
      ? `Rs. ${formatted}`   // PDF-safe
      : `₹ ${formatted}`;   // Excel-safe
  }


}
